/**
 * Import History — Email / Export dialogs (Angular `EmailOptionComponent` + `sendEmail`).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { AdvancedDateRangePicker, ANGULAR_COMMUNICATION_LOG_PRESETS } from '@/components/ui/AdvancedDateRangePicker'
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/SearchableSelect'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { showError, showSuccess } from '@/lib/utils/notifications'
import { format } from 'date-fns'
import {
  buildAlnExportUrl,
  postAlnMail,
  searchLibertyUsers,
} from '../services/importDataService'
import { createDefaultImportFilterState } from '../utils/importDataDateDefaults'
import { primaryEntityActionButtonClass, secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'

import type { AlnImportHistoryMailModalProps } from '../types'

export function AlnImportHistoryMailModal({
  isOpen,
  mode,
  onClose,
  page,
  importType,
}: AlnImportHistoryMailModalProps) {
  const user = useAppSelector(selectUser)
  const showPassthru = typeof window !== 'undefined' && window.location.hostname !== 'cornerstone.libertyrent.com'

  const [exportStart, setExportStart] = useState<Date | null>(null)
  const [exportEnd, setExportEnd] = useState<Date | null>(null)
  const [exporting, setExporting] = useState(false)

  const [userOptions, setUserOptions] = useState<SearchableSelectOption[]>([])
  const [userSearchLoading, setUserSearchLoading] = useState(false)
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const [sendSeparate, setSendSeparate] = useState<'0' | '1'>('0')
  const [replyTo, setReplyTo] = useState('')
  const [mailStart, setMailStart] = useState<Date | null>(null)
  const [mailEnd, setMailEnd] = useState<Date | null>(null)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailBody, setEmailBody] = useState('')
  const [passthruMailgun, setPassthruMailgun] = useState<'0' | '1'>('0')
  const [mailSubmitting, setMailSubmitting] = useState(false)

  const resetExport = useCallback(() => {
    setExportStart(null)
    setExportEnd(null)
  }, [])

  const resetEmail = useCallback(() => {
    setSelectedUserIds([])
    setSendSeparate('0')
    setReplyTo(user?.email ?? '')
    setMailStart(null)
    setMailEnd(null)
    setEmailSubject('')
    setEmailBody('')
    setPassthruMailgun('0')
    setUserOptions([])
  }, [user?.email])

  const handleSearchUsers = useCallback(async (q: string) => {
    setUserSearchLoading(true)
    try {
      const opts = await searchLibertyUsers(q)
      setUserOptions(opts)
    } catch {
      setUserOptions([])
    } finally {
      setUserSearchLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return
    if (mode === 'export') {
      resetExport()
    } else if (mode === 'email') {
      resetEmail()
      const defaults = createDefaultImportFilterState()
      setMailStart(defaults.dateStart)
      setMailEnd(defaults.dateEnd)
    }
  }, [isOpen, mode, resetExport, resetEmail])

  /** Angular `searchUser` with empty query still loads `organization_type=LIBERTY` users. */
  useEffect(() => {
    if (!isOpen || mode !== 'email') return
    void handleSearchUsers('')
  }, [isOpen, mode, handleSearchUsers])

  const handleExportSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!importType) {
      showError('Select an upload file type first.', false)
      return
    }
    setExporting(true)
    try {
      const url = buildAlnExportUrl(page, importType, exportStart, exportEnd)
      if (!url) {
        showError('Not signed in or export URL could not be built.', false)
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
      onClose()
    } finally {
      setExporting(false)
    }
  }

  const handleMailSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!selectedUserIds.length) {
      showError('Please select user to send email', false)
      return
    }
    if (!emailSubject.trim() || !emailBody.trim()) {
      showError('Subject and email body are required.', false)
      return
    }
    if (!mailStart || !mailEnd) {
      showError('Select a date range for the report.', false)
      return
    }
    setMailSubmitting(true)
    try {
      const fd = new FormData()
      fd.append('seperated_to_address', sendSeparate)
      fd.append('email_subject', emailSubject.trim())
      fd.append('email_body', emailBody)
      fd.append('reply_to', encodeURIComponent(replyTo.trim()))
      fd.append('passthru_mailgun', passthruMailgun)
      selectedUserIds.forEach((id, index) => {
        fd.append(`user_ids[${index}]`, id)
      })
      fd.append('fromDt', format(mailStart, 'dd-MM-yyyy'))
      fd.append('toDt', format(mailEnd, 'dd-MM-yyyy'))
      fd.append('frompage', 'users')
      await postAlnMail(fd)
      showSuccess('Mail sent successfully')
      onClose()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to send mail', false)
    } finally {
      setMailSubmitting(false)
    }
  }

  if (!mode) return null

  const title = mode === 'export' ? 'Report Export' : 'Report Email'

  return (
    <OnScreenModal
      isOpen={isOpen}
      title={title}
      onClose={onClose}
      maxWidth="max-w-3xl"
      overlayDataTestId="import-history-mail-modal-overlay"
      panelDataTestId="import-history-mail-modal-panel"
      bodyClassName="max-h-[min(85vh,720px)] overflow-y-auto"
    >
      {mode === 'export' ? (
        <form onSubmit={handleExportSubmit} className="space-y-4 text-sm">
          <p className="text-slate-600">
            Optionally narrow the export with a date range (same filters as Angular export dialog).
          </p>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Date range</label>
            <AdvancedDateRangePicker
              startDate={exportStart}
              endDate={exportEnd}
              onChange={(s, ed) => {
                setExportStart(s)
                setExportEnd(ed)
              }}
              placeholder="Select date range"
              customPresets={ANGULAR_COMMUNICATION_LOG_PRESETS}
              monthsShown={2}
              displayRangeFormat="angularMmddyyyy"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={cn(secondaryEntityActionButtonClass, "px-4 py-2")}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={exporting || !importType}
              className={cn(primaryEntityActionButtonClass, "px-4 py-2 flex items-center gap-2")}
            >
              {exporting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Export
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleMailSubmit} className="space-y-4 text-sm">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Select users</label>
            <SearchableSelect
              multiple
              options={userOptions}
              value={selectedUserIds}
              onChange={(v) => setSelectedUserIds(Array.isArray(v) ? v : v ? [v] : [])}
              placeholder="All user"
              searchPlaceholder="Search by name, email"
              onSearch={handleSearchUsers}
              loading={userSearchLoading}
              triggerDataTestId="import-history-email-user-select"
            />
          </div>

          <fieldset>
            <legend className="mb-2 text-xs font-semibold text-slate-500">Send separately</legend>
            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="seperated_to_address"
                  checked={sendSeparate === '1'}
                  onChange={() => setSendSeparate('1')}
                  className="border-slate-400"
                />
                Yes
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="seperated_to_address"
                  checked={sendSeparate === '0'}
                  onChange={() => setSendSeparate('0')}
                  className="border-slate-400"
                />
                No
              </label>
            </div>
          </fieldset>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Reply to</label>
            <input
              type="text"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase text-slate-500">Date range</label>
            <AdvancedDateRangePicker
              startDate={mailStart}
              endDate={mailEnd}
              onChange={(s, ed) => {
                setMailStart(s)
                setMailEnd(ed)
              }}
              placeholder="Select date range"
              customPresets={ANGULAR_COMMUNICATION_LOG_PRESETS}
              monthsShown={2}
              displayRangeFormat="angularMmddyyyy"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Email subject</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Email body</label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              required
              rows={8}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-sans text-sm"
            />
          </div>

          {showPassthru ? (
            <fieldset>
              <legend className="mb-2 text-xs font-semibold text-slate-500">Passthru Mailgun</legend>
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="passthru_mailgun"
                    checked={passthruMailgun === '1'}
                    onChange={() => setPassthruMailgun('1')}
                    className="border-slate-400"
                  />
                  Yes
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="passthru_mailgun"
                    checked={passthruMailgun === '0'}
                    onChange={() => setPassthruMailgun('0')}
                    className="border-slate-400"
                  />
                  No
                </label>
              </div>
            </fieldset>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className={cn(secondaryEntityActionButtonClass, "px-4 py-2")}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mailSubmitting}
              className={cn(primaryEntityActionButtonClass, "px-4 py-2 flex items-center gap-2")}
            >
              {mailSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              Submit
            </button>
          </div>
        </form>
      )}
    </OnScreenModal>
  )
}
